import { baseURL } from 'settings';
import { api as apiService, ApiService } from './api';
import {
  IDefaultRouteCostsResponse,
  IDefaultRouteCostsData,
  IVehicleTypeResponse
} from 'pages/CostManagement/DefaultCosts/types';
import convertObjectToSnakeCase from 'utils/toSnakeCase';
import { ILastMile } from 'pages/CostManagement/LastMile';
import { PaginationProps } from 'interfaces/IPagination';
import { ISupportCost } from 'pages/CostManagement/Support';
import qs from 'qs';

interface IFirstMile {
  id: number;
  transshipPoint: {
    id: number;
    name: string;
  };
  vehicle: {
    id: number;
    driver: {
      id: number;
      name: string;
      surname: string;
    };
    licensePlate: string;
  };
  vehicleCost: number;
  costAdjustment: number;
  deliveryDate: string;
  leaderCommission: number;
}

interface IcreateFirstMileCost {
  transshipPointId: number;
  vehicleId: number;
  vehicleCost: number;
  costAdjustment: number;
  deliveryDate: string;
  leaderCommission: number;
}

class CostManagementService {
  constructor(
    private readonly api: ApiService,
    private readonly baseURL: string,
  ) {}

  public getDefaultCosts = async (
    params: { groupId?: number },
  ): Promise<IDefaultRouteCostsResponse[]> => {
    return await this.api.get(
      `${this.baseURL}/v1/cost-management/default-route-costs`,
      { params },
    );
  };

  public createDefaultCost = async (
    data: IDefaultRouteCostsData,
  ): Promise<IDefaultRouteCostsResponse> => {
    const snakeCaseData = convertObjectToSnakeCase(data);
    return await this.api.post(
      `${this.baseURL}/v1/cost-management/default-route-costs`,
      snakeCaseData,
    );
  };

  public updateDefaultCost = async (
    data: IDefaultRouteCostsData,
  ): Promise<IDefaultRouteCostsResponse> => {
    const snakeCaseData = convertObjectToSnakeCase(data);
    return await this.api.patch(
      `${this.baseURL}/v1/cost-management/default-route-costs`,
      snakeCaseData,
    );
  };

  public deleteDefaultCost = async (
    groupId: number,
  ): Promise<void> => {
    return await this.api.delete(
      `${this.baseURL}/v1/cost-management/default-route-costs/${groupId}`,
    );
  };

  public getVehicleTypes = async (): Promise<IVehicleTypeResponse[]> => {
    return await this.api.get(
      `${this.baseURL}/v1/cost-management/vehicle-type`,
    );
  };

  public getFirstMileCost = async (params: {
    startDate: string;
    endDate: string;
    search?: string;
    transshipPointIds?: number[];
    teamIds?: number[];
    page?: number;
    pageSize?: number;
  }): Promise<PaginationProps<IFirstMile>> => {
    return await this.api.get(
      `${this.baseURL}/v1/cost-management/first-mile-costs`,
      {
        params,
        paramsSerializer: (params) =>
          qs.stringify(params, { arrayFormat: 'repeat' }),
      },
    );
  };

  public getLastMileCost = async (params: {
    startDate: string;
    endDate: string;
    search?: string;
    transshipPointIds?: number[];
    teamIds?: number[];
    page?: number;
    pageSize?: number;
  }): Promise<PaginationProps<ILastMile>> => {
    return await this.api.get(
      `${this.baseURL}/v1/cost-management/last-mile-costs`,
      {
        params,
        paramsSerializer: (params) =>
          qs.stringify(params, { arrayFormat: 'repeat' }),
      },
    );
  };

  public createFirstMileCost = async (
    data: IcreateFirstMileCost,
  ): Promise<IFirstMile> => {
    const snakeCaseData = convertObjectToSnakeCase(data);
    return await this.api.post(
      `${this.baseURL}/v1/cost-management/first-mile-costs`,
      snakeCaseData,
    );
  };

  public updateFirstMileCostAdjustment = async (
    id: number,
    adjustment: number,
  ): Promise<IFirstMile> => {
    return await this.api.patch(
      `${this.baseURL}/v1/cost-management/first-mile-costs/${id}`,
      {
        cost_adjustment: adjustment,
      },
    );
  };

  public updateFirstMileCostBatch = async (payload: {
    costs: any[];
  }): Promise<any> => {
    return await this.api.put(
      `${this.baseURL}/v1/cost-management/first-mile-costs`,
      payload,
    );
  };

  public updateLastMileCostBatch = async (payload: {
    costs: any[];
  }): Promise<any> => {
    return await this.api.put(
      `${this.baseURL}/v1/cost-management/last-mile-costs`,
      payload,
    );
  };

  public deleteFirstMileCost = async (payload: {
    ids: number[];
  }): Promise<void> => {
    return await this.api.delete(
      `${this.baseURL}/v1/cost-management/first-mile-costs`,
      { data: payload },
    );
  };

  public getSupportCost = async (params: {
    startDate: string;
    endDate: string;
    search?: string;
    transshipPointIds?: number[];
    teamIds?: number[];
    page?: number;
    pageSize?: number;
  }): Promise<PaginationProps<ISupportCost>> => {
    return await this.api.get(
      `${this.baseURL}/v1/cost-management/support-driver-costs`,
      {
        params,
        paramsSerializer: (params) =>
          qs.stringify(params, { arrayFormat: 'repeat' }),
      },
    );
  };

  public updateSupportCostBatch = async (payload: {
    costs: any[];
  }): Promise<any> => {
    return await this.api.put(
      `${this.baseURL}/v1/cost-management/support-driver-costs`,
      payload,
    );
  };

  public exportCosts = async (type: 'first_mile' | 'last_mile' | 'support', params: {
    startDate: string;
    endDate: string;
    search?: string;
    transshipPointIds?: number[];
    teamIds?: number[];
    active?: boolean;
  }): Promise<any> => {
    return await this.api.get(`${this.baseURL}/v1/cost-management/export-costs/${type}`, {
      responseType: 'arraybuffer',
      params,
      paramsSerializer: (params: any) =>
        qs.stringify(params, { arrayFormat: 'repeat' }),
    },);
  };
}

export const costManagementService = new CostManagementService(
  apiService,
  baseURL,
);
